import { AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { FormComponentPropLanguages } from "components/Form/components/FormComponentProps";
import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { requiredValidator } from "components/Form/Form.utils";
import { FormField } from "config/config";
import {
    valueAndSchemaInput,
    valueAndSchemaOutput,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { Execution } from "graphql/generated/graphql";
import { YAMLException } from "js-yaml";
import { isEqual } from "lodash";
import { getRelativeUrl } from "utils/service";
import { parseYaml } from "utils/valueTransformers";

// A scheduler's configuration_schema describes the schedule configuration,
// not free flow inputs, so it carries its own label and docs link.
const isConfigurationSchema = (fieldName: string) =>
    fieldName === "configuration_schema";

export const valueAndSchemaInputField = (fieldName = "input_schema") => ({
    name: valueAndSchemaInput,
    component: "schemaForm", // TODO: use form with input_schema https://app.clickup.com/t/860q6x5p1
    fieldProps: {
        lang: "yaml" as FormComponentPropLanguages,
        isReadOnly: false,
        label: isConfigurationSchema(fieldName)
            ? "Configuration schema"
            : "Input schema",
        docs: isConfigurationSchema(fieldName)
            ? {
                  text: "Scheduling",
                  urlPath: "/docs/scheduling#scheduler-configuration-schema",
              }
            : {
                  text: "Schema creation and validation",
                  urlPath:
                      "/docs/schema-creation-and-validation#input-and-output-schema",
              },
        columnDirection: true,
        testFormButton: true,
        canEnableSaveManually: true,
    },
    remappingSubmitDto: (value: SchematizedValues) => {
        if (value) {
            return [
                {
                    key: fieldName,
                    value: value.values,
                },
            ];
        } else return [];
    },
    isEqual: (valueA, valueB) => {
        return isEqual(valueA, valueB);
    },
    validation: (value) => {
        try {
            parseYaml(value.values);
            return undefined;
        } catch (error: any) {
            const typedError: YAMLException = error;
            const errorMessage = `${typedError?.name}: ${typedError?.reason}`;
            return errorMessage;
        }
    },
});

export const valueAndSchemaOutputField = {
    name: valueAndSchemaOutput,
    component: "schemaForm", // TODO: use form with input_schema https://app.clickup.com/t/860q6x5p1
    fieldProps: {
        lang: "yaml" as FormComponentPropLanguages,
        isReadOnly: false,
        label: "Output schema",
        docs: {
            text: "Schema creation and validation",
            urlPath: "/docs/schema-creation-and-validation#input-and-output-schema",
        },
        columnDirection: true,
        testFormButton: true,
        canEnableSaveManually: true,
    },
    remappingSubmitDto: (value: SchematizedValues) => {
        if (value) {
            return [
                {
                    key: "output_schema",
                    value: value.values,
                },
            ];
        } else return [];
    },
    isEqual: (valueA, valueB) => {
        return isEqual(valueA, valueB);
    },
    validation: (value) => {
        try {
            parseYaml(value.values);
            return undefined;
        } catch (error: any) {
            const typedError: YAMLException = error;
            const errorMessage = `${typedError?.name}: ${typedError?.reason}`;
            return errorMessage;
        }
    },
};

const specialExecutionLocationValue = { value: "special_value", label: "Specific project" };

export const executionLocationField = ({
    optionList,
    docs,
}: {
    optionList: AutocompleteOption[];
    docs?: { text: string; urlPath: string };
}): FormField => ({
    name: "execution_location",
    component: "extendedAutocomplete",
    fieldProps: {
        label: "Execution location",
        options: {
            list: [...optionList, specialExecutionLocationValue],
        },
        extendedAutocompleteOptions: {
            resource: "project",
        },
        specialValue: specialExecutionLocationValue,
        clearable: false,
        docs,
    },
    fieldAttr: { required: true },
    validation: requiredValidator,
});

export const latestListContainerWidget = (
    uid: string,
    getLink: (resourceId: string) => string
) => ({
    uid,
    title: "Latest executions",
    className: "w-1/3",
    component: "LatestList",
    link: getLink,
    dataTestId: "latestExecutions",
    dto: ({ stats }: any) => [
        ...(stats?.latestExecutions?.execution_list?.execution_list?.map((item: Execution) => ({
            ...item,
            link: getRelativeUrl("execution", item.id),
        })) || []),
    ],
});

export const scriptField = {
    name: "script",
    component: "visualCodeEditor",
    fieldProps: {
        lang: "python" as "python",
        isReadOnly: false,
        initialHeight: "48vh",
        breakpointsEnabled: true,
        columnDirection: true,
    },
    fieldAttr: {},
};
